import { get, writable } from "svelte/store";
import API from "../../API/API";
import Feature from "../../Branding/Feature";
import UserHelper from "../../Branding/User";
import LoadState from "../../Enums/LoadState";
import Company from "../../Models/Company";
import Image from "../../Models/Image";
import ImageCollection from "../../Models/ImageCollection";
import ROI from "../../Models/ROI";
import Tier from "../../Models/Tier";
import User from "../../Models/User";
import VesselCommentThread from "../../Models/VesselCommentThread";
import ConfirmDialogData from "../ConfirmDialog/ConfirmDialogData";

const AdminStore = {
	cms: {
		meta: {
			loadState: writable(LoadState.INIT)
		},
		visible: writable(false),
		users: writable([]),
		companies: writable([]),
		tiers: writable([]),
		rois: writable([]),
		images: writable([]),
		imageCollections: writable([]),
		vesselCommentThreads: writable({}), // Arrays indexed by spire id
		vesselCommentThreadDetails: writable({}), // Arrays indexed by spire id
		alertTypes: writable([]),
		unattributedUsers: writable([]),

		confirmDialog: new ConfirmDialogData()
	},
	actions: {

	},
	helpers: {

	}
};

AdminStore.actions.refresh = () => {
	AdminStore.cms.meta.loadState.set(LoadState.LOADING);

	let count = 0;
	const _increment = (i = 1) => {
		count += i;
	};
	const _decrement = (i = 1) => {
		count -= i;

		if (count === 0) {
			AdminStore.cms.meta.loadState.set(LoadState.LOADED);
		}
	};

	if (UserHelper.hasAnyOfFeatures([Feature.ADMIN.EDIT, Feature.ADMIN.USERS.VIEW, Feature.ADMIN.USERS.EDIT])) {
		_increment(2);
		API.get("admin/users")
			.then((response) => {
				AdminStore.cms.users.set(response.map((item) => {
					return new User(item);
				}));
				_decrement();
			});
		API.get("admin/users/unattributed")
			.then((response) => {
				AdminStore.cms.unattributedUsers.set(response.map((item) => {
					return {
						company_name: "",
						...item
					};
				}));
				_decrement();
			});
	}
	if (UserHelper.hasAnyOfFeatures([Feature.ADMIN.EDIT, Feature.ADMIN.COMPANIES.VIEW, Feature.ADMIN.COMPANIES.EDIT])) {
		_increment();
		API.get("admin/companies")
			.then((response) => {
				AdminStore.cms.companies.set(response.map((item) => {
					return new Company(item);
				}));
				_decrement();
			});
	}

	if (UserHelper.hasAnyOfFeatures([Feature.ADMIN.EDIT, Feature.ADMIN.TIERS.VIEW, Feature.ADMIN.TIERS.EDIT])) {
		_increment();
		API.get("admin/tiers")
			.then((response) => {
				AdminStore.cms.tiers.set(response.map((item) => {
					return new Tier(item);
				}));
				_decrement();
			});
	}

	if (UserHelper.hasAnyOfFeatures([Feature.ADMIN.EDIT, Feature.ADMIN.ROIS.VIEW, Feature.ADMIN.ROIS.EDIT])) {
		_increment();
		API.get("admin/rois?full=false")
			.then((response) => {
				AdminStore.cms.rois.set(response.map((item) => {
					return new ROI(item);
				}));
				_decrement();
			});
	}

	// if (true) {
	_increment();
	API.get("admin/alert-types")
		.then((response) => {
			AdminStore.cms.alertTypes.set(response.map((item) => {
				return item;
			}));
			_decrement();
		});
	// }

	if (UserHelper.hasAnyOfFeatures([Feature.ADMIN.EDIT, Feature.ADMIN.IMAGES.VIEW, Feature.ADMIN.IMAGES.EDIT])) {
		_increment(2);

		API.get("admin/images")
			.then((response) => {
				AdminStore.cms.images.set(response.map((item) => {
					return new Image(item);
				}));
				_decrement();
			});

		API.get("admin/image-sets")
			.then((response) => {
				AdminStore.cms.imageCollections.set(response.map((item) => {
					return new ImageCollection(item);
				}));
				_decrement();
			});
	}

	if (UserHelper.hasFeature(Feature.ADMIN.EDIT) || UserHelper.hasFeature(Feature.ADMIN.COMMENTS.VIEW)) {
		_increment();
		API.get("admin/vessel-comments")
			.then((response) => {
				const { threadBySpireId, detailsBySpireId } = VesselCommentThread.helpers.processList(response.threads);
				AdminStore.cms.vesselCommentThreads.set(threadBySpireId);
				AdminStore.cms.vesselCommentThreadDetails.set(detailsBySpireId);
				_decrement();
			});
	}
};

AdminStore.actions.addTier = (tier) => {
	AdminStore.actions._addItem(tier, AdminStore.cms.tiers);
};

AdminStore.actions.deleteTier = (tier) => {
	AdminStore.actions._deleteItem(tier, "id", AdminStore.cms.tiers);
};

AdminStore.actions.addUser = (user) => {
	AdminStore.actions._addItem(user, AdminStore.cms.users);
};

AdminStore.actions.deleteUser = (user) => {
	AdminStore.actions._deleteItem(user, "id", AdminStore.cms.users);
};

AdminStore.actions.deleteUnattributedUser = (uuser) => {
	AdminStore.actions._deleteItem(uuser, "cognito_id", AdminStore.cms.unattributedUsers);
};

AdminStore.actions.addCompany = (company) => {
	AdminStore.actions._addItem(company, AdminStore.cms.companies);
};

AdminStore.actions.deleteCompany = (company) => {
	AdminStore.actions._deleteItem(company, "id", AdminStore.cms.companies);
};

AdminStore.actions.addImage = (image) => {
	AdminStore.actions._addItem(image, AdminStore.cms.images);
};

AdminStore.actions.deleteImage = (image) => {
	AdminStore.actions._deleteItem(image, "id", AdminStore.cms.images);
};

AdminStore.actions.addImageCollection = (imageCollection) => {
	AdminStore.actions._addItem(imageCollection, AdminStore.cms.imageCollections);
};

AdminStore.actions.deleteImageCollection = (imageCollection) => {
	AdminStore.actions._deleteItem(imageCollection, "id", AdminStore.cms.imageCollections);
};

AdminStore.actions.addROI = (roi) => {
	AdminStore.actions._addItem(roi, AdminStore.cms.rois);
};

AdminStore.actions.deleteROI = (roi) => {
	AdminStore.actions._deleteItem(roi, "id", AdminStore.cms.rois);
};

AdminStore.actions._addItem = (model, storeCollection) => {
	storeCollection.update((models) => {
		return [...models, model];
	});
};
AdminStore.actions._deleteItem = (model, property, storeCollection) => {
	storeCollection.update((models) => {
		for (let i = 0; i < models.length; i++) {
			if (models[i][property] === model[property]) {
				models.splice(i, 1);
				break;
			}
		}
		return models;
	});
};

AdminStore.helpers._getItemByProperty = (property, value, storeCollection) => {
	// _.pluck?
	const items = get(storeCollection);
	for (let i = 0; i < items.length; i++) {
		if (items[i][property] === value) {
			return items[i];
		}
	}
	console.warn("AdminStore.helpers._getItemByProperty return null", property, value, items);
	return null;
};

AdminStore.helpers.getUserById = (id) => {
	return AdminStore.helpers._getItemByProperty("id", id, AdminStore.cms.users);
};
AdminStore.helpers.getCompanyById = (id) => {
	return AdminStore.helpers._getItemByProperty("id", id, AdminStore.cms.companies);
};
AdminStore.helpers.getCompanyByName = (name) => {
	return AdminStore.helpers._getItemByProperty("name", name, AdminStore.cms.companies);
};

AdminStore.helpers.getTierByName = (name) => {
	return AdminStore.helpers._getItemByProperty("name", name, AdminStore.cms.tiers);
};
AdminStore.helpers.getTierById = (id) => {
	return AdminStore.helpers._getItemByProperty("id", id, AdminStore.cms.tiers);
};
AdminStore.helpers.getROIById = (id) => {
	return AdminStore.helpers._getItemByProperty("id", id, AdminStore.cms.rois);
};
AdminStore.helpers.getImageById = (id) => {
	return AdminStore.helpers._getItemByProperty("id", id, AdminStore.cms.images);
};

export default AdminStore;
